import { useEffect, useMemo, useState, FormEvent } from 'react';
import {
  ArrowLeftRight,
  Plus,
  Wallet,
  Pencil,
  Trash2,
  AlertTriangle,
  ArrowUpRight,
} from 'lucide-react';
import api from '../services/api';
import { PageLoading } from '../components/common/Loading';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';

interface Envelope {
  id: number;
  name: string;
  category?: string | null;
  budgeted: number;
  spent?: number;
  remaining?: number;
  notes?: string | null;
  created_at: string;
}

const CATEGORIES = [
  'Housing',
  'Transportation',
  'Food',
  'Utilities',
  'Insurance',
  'Healthcare',
  'Savings',
  'Personal',
  'Entertainment',
  'Debt Payments',
  'Other',
];

export default function Envelopes() {
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [editingEnvelope, setEditingEnvelope] = useState<Envelope | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<Envelope | null>(null);

  const [totalBudgeted, setTotalBudgeted] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);

  useEffect(() => {
    void loadEnvelopes();
    void loadIncome();
  }, []);

  const loadEnvelopes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ envelopes: Envelope[]; total_budgeted: number }>(
        '/envelopes?include_stats=true'
      );
      setEnvelopes(response.envelopes || []);
      setTotalBudgeted(response.total_budgeted || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load envelopes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadIncome = async () => {
    try {
      const response = await api.get<{ monthly_total: number }>('/incomes');
      setMonthlyIncome(response.monthly_total || 0);
    } catch {
      // ignore
    }
  };

  const allocation = useMemo(() => {
    const availableToBudget = monthlyIncome - totalBudgeted;
    const allocationPercentage = monthlyIncome > 0 ? (totalBudgeted / monthlyIncome) * 100 : 0;
    return {
      availableToBudget,
      allocationPercentage,
      isOverAllocated: allocationPercentage > 100,
    };
  }, [monthlyIncome, totalBudgeted]);

  const handleDelete = async (envelope: Envelope) => {
    try {
      await api.delete(`/envelopes/${envelope.id}`);
      await loadEnvelopes();
    } catch (err: any) {
      setError(err.message || 'Failed to delete envelope');
    } finally {
      setConfirmDelete(null);
    }
  };

  const openCreate = () => {
    setEditingEnvelope(null);
    setShowForm(true);
  };

  const openEdit = (envelope: Envelope) => {
    setEditingEnvelope(envelope);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingEnvelope(null);
  };

  const closeTransfer = () => setShowTransfer(false);

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Envelopes"
        subtitle="Allocate income into categories and track monthly spending"
        actions={
          <>
            {envelopes.length >= 2 && (
              <button onClick={() => setShowTransfer(true)} className="btn btn-secondary">
                <ArrowLeftRight className="w-4 h-4" />
                Transfer
              </button>
            )}
            <button onClick={openCreate} className="btn btn-primary shadow-glow">
              <Plus className="w-4 h-4" />
              Add envelope
            </button>
          </>
        }
      />

      {error && (
        <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <Card
        title="Budget allocation"
        subtitle="How much of your monthly income is currently assigned"
        actions={
          <div className="text-right">
            <p className="text-xs text-text-muted">Available to budget</p>
            <p className={`text-lg font-semibold ${allocation.availableToBudget >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(allocation.availableToBudget)}
            </p>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">Allocation</span>
            <span className="text-sm font-medium text-text-header">{allocation.allocationPercentage.toFixed(0)}%</span>
          </div>

          <div className="w-full bg-background-subtle rounded-full h-3 overflow-hidden border border-border">
            <div
              className={`h-3 rounded-full transition-all duration-200 ${allocation.isOverAllocated ? 'bg-danger' : 'bg-primary'}`}
              style={{ width: `${Math.min(allocation.allocationPercentage, 100)}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>{formatCurrency(totalBudgeted)} budgeted</span>
            <span>of {formatCurrency(monthlyIncome)} income</span>
          </div>

          {allocation.isOverAllocated && (
            <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
              <p className="text-sm text-text-body">
                <span className="font-semibold text-text-header">Over-allocated:</span> You’ve budgeted more than your monthly income.
                Consider reducing some envelopes.
              </p>
            </div>
          )}
        </div>
      </Card>

      {envelopes.length === 0 ? (
        <Card title="No envelopes yet" subtitle="Create envelopes to organize your budget." className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <button onClick={openCreate} className="btn btn-primary shadow-glow inline-flex">
            <Plus className="w-4 h-4" />
            Create first envelope
          </button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {envelopes.map((env) => (
            <EnvelopeCard
              key={env.id}
              envelope={env}
              onEdit={() => openEdit(env)}
              onDelete={() => setConfirmDelete(env)}
            />
          ))}
        </div>
      )}

      <EnvelopeFormModal
        isOpen={showForm}
        envelope={editingEnvelope}
        onClose={closeForm}
        onSubmit={async () => {
          await loadEnvelopes();
          closeForm();
        }}
      />

      <TransferModal
        isOpen={showTransfer}
        envelopes={envelopes}
        onClose={closeTransfer}
        onSubmit={async () => {
          await loadEnvelopes();
          closeTransfer();
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && void handleDelete(confirmDelete)}
        title="Delete envelope"
        message={
          confirmDelete
            ? `Delete “${confirmDelete.name}”? Transactions will become unassigned. If it has transactions, deletion may be blocked — archive instead.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function EnvelopeCard({
  envelope,
  onEdit,
  onDelete,
}: {
  envelope: Envelope;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const spent = envelope.spent || 0;
  const remaining = envelope.remaining ?? envelope.budgeted - spent;
  const percentage = envelope.budgeted > 0 ? (spent / envelope.budgeted) * 100 : 0;
  const progress = Math.min(percentage, 100);

  const status =
    percentage >= 100 ? (
      <Badge variant="danger">Over</Badge>
    ) : percentage >= 80 ? (
      <Badge variant="warning">Watch</Badge>
    ) : (
      <Badge variant="success">On track</Badge>
    );

  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-text-header truncate">{envelope.name}</h3>
            {status}
          </div>
          {envelope.category && <p className="mt-1 text-sm text-text-muted truncate">{envelope.category}</p>}
        </div>

        <div className="flex items-center gap-2">
          <button className="btn btn-secondary px-3 py-2" onClick={onEdit} title="Edit">
            <Pencil className="w-4 h-4" />
          </button>
          <button className="btn btn-danger px-3 py-2" onClick={onDelete} title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-baseline justify-between">
          <p className={`text-2xl font-bold ${remaining >= 0 ? 'text-text-header' : 'text-danger'}`}>
            {formatCurrency(remaining)}
          </p>
          <p className="text-sm text-text-muted">left</p>
        </div>

        <div className="mt-3 w-full bg-background-subtle rounded-full h-2 overflow-hidden border border-border">
          <div
            className={`h-2 rounded-full transition-all duration-200 ${
              percentage >= 100 ? 'bg-danger' : percentage >= 80 ? 'bg-warning' : 'bg-primary'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-text-muted">
          <span>{formatCurrency(spent)} spent</span>
          <span>of {formatCurrency(envelope.budgeted)}</span>
        </div>

        {percentage >= 100 && (
          <div className="mt-4 p-3 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-2">
            <ArrowUpRight className="w-4 h-4 text-danger mt-0.5" />
            <p className="text-xs text-text-body">You’ve hit or exceeded the budgeted amount for this month.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function EnvelopeFormModal({
  isOpen,
  envelope,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  envelope: Envelope | null;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    name: envelope?.name || '',
    category: envelope?.category || '',
    budgeted: envelope?.budgeted?.toString() || '',
    notes: envelope?.notes || '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData({
      name: envelope?.name || '',
      category: envelope?.category || '',
      budgeted: envelope?.budgeted?.toString() || '',
      notes: envelope?.notes || '',
    });
    setError('');
    setIsSubmitting(false);
  }, [envelope, isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    const budgeted = parseFloat(formData.budgeted);
    if (Number.isNaN(budgeted) || budgeted < 0) {
      setError('Please enter a valid budget amount');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category || null,
        budgeted,
        notes: formData.notes.trim() || null,
      };

      if (envelope) {
        await api.put(`/envelopes/${envelope.id}`, payload);
      } else {
        await api.post('/envelopes', payload);
      }

      await onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to save envelope');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={envelope ? 'Edit envelope' : 'Create envelope'}
      description="Create categories to allocate your monthly budget."
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="envelope-form" disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Saving…' : envelope ? 'Update' : 'Create'}
          </button>
        </div>
      }
    >
      <form id="envelope-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Envelope name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input-field"
            placeholder="e.g., Groceries, Gas, Entertainment"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Category (optional)</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="input-field"
          >
            <option value="">Select a category</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Monthly budget</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.budgeted}
              onChange={(e) => setFormData({ ...formData, budgeted: e.target.value })}
              className="input-field pl-7"
              placeholder="0.00"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Notes (optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="input-field"
            placeholder="Any notes about this envelope…"
          />
        </div>
      </form>
    </Modal>
  );
}

function TransferModal({
  isOpen,
  envelopes,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  envelopes: Envelope[];
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    from_envelope_id: '',
    to_envelope_id: '',
    amount: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({ from_envelope_id: '', to_envelope_id: '', amount: '' });
    setError('');
    setIsSubmitting(false);
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.from_envelope_id || !formData.to_envelope_id) {
      setError('Please select both envelopes');
      return;
    }

    if (formData.from_envelope_id === formData.to_envelope_id) {
      setError('Cannot transfer to the same envelope');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/envelopes/transfer', {
        from_envelope_id: parseInt(formData.from_envelope_id, 10),
        to_envelope_id: parseInt(formData.to_envelope_id, 10),
        amount,
      });

      await onSubmit();
    } catch (err: any) {
      setError(err.message || 'Failed to transfer');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transfer"
      description="Move available funds between envelopes."
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="transfer-form" disabled={isSubmitting} className="btn btn-primary shadow-glow">
            {isSubmitting ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      }
    >
      <form id="transfer-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">From</label>
          <select
            value={formData.from_envelope_id}
            onChange={(e) => setFormData({ ...formData, from_envelope_id: e.target.value })}
            className="input-field"
          >
            <option value="">Select envelope</option>
            {envelopes.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name} ({formatCurrency(env.remaining ?? env.budgeted)})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">To</label>
          <select
            value={formData.to_envelope_id}
            onChange={(e) => setFormData({ ...formData, to_envelope_id: e.target.value })}
            className="input-field"
          >
            <option value="">Select envelope</option>
            {envelopes.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-muted mb-2">Amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="input-field pl-7"
              placeholder="0.00"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
